import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES_LIST } from "@/data/services";
import { SERVICES } from "@/constants/testIds";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Five primary practices. Each rendered as an explicit, boxed-off SECTION with
 * strong dividers, a "SERVICE N / 4" banner, alternating photography, and a
 * sub-services grid. Zero ambiguity that these are five distinct sections.
 */
export const ServicesManifesto = ({ showIntro = true }) => {
  return (
    <section className="relative bg-[var(--bg)]">
      {showIntro && (
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline mb-4">The manifesto</p>
            </div>
            <div className="md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.9, ease: EASE }}
                className="font-display text-4xl md:text-6xl tracking-[-0.02em] leading-[0.98] text-[var(--ink)]"
              >
                Five primary practices,{" "}
                <span className="font-serif-accent italic text-[var(--accent)]">
                  one partner
                </span>{" "}
                across them all.
              </motion.h2>
              <p className="mt-6 max-w-2xl text-[var(--ink-70)] text-base md:text-lg leading-relaxed">
                Each practice below is a self-contained engagement — scope it
                individually, or combine two or three under a single fractional
                HR arrangement.
              </p>
            </div>
          </div>
        </div>
      )}

      {SERVICES_LIST.map((s, i) => {
        const reverse = i % 2 === 1;
        const surface = i % 2 === 0 ? "bg-[var(--bg)]" : "bg-[var(--bg-2)]";
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={SERVICES.chapter(s.number)}
            className={`${surface} border-t border-[var(--line-strong)]`}
          >
            {/* Section banner */}
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-4 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[var(--ink-50)]">
              <span className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[var(--accent)] rounded-full" />
                Service {s.number} / 0{SERVICES_LIST.length}
              </span>
              <span className="hidden md:inline">{s.tagline}</span>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
              <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
                {/* Left: giant number */}
                <div className={`md:col-span-2 ${reverse ? "md:order-3" : ""}`}>
                  <div className="font-serif-accent italic text-[clamp(6rem,12vw,11rem)] leading-none text-[var(--ink)]/[0.10] select-none">
                    {s.number}
                  </div>
                </div>

                {/* Middle: image */}
                <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
                  <div className="clip-frame aspect-[4/5]">
                    <img src={s.image} alt={s.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-white border border-white/50 px-2 py-1 bg-black/25 backdrop-blur-sm">
                      Practice {s.number}
                    </div>
                  </div>
                </div>

                {/* Right: text */}
                <div className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}>
                  <p className="overline mb-4">{s.tagline}</p>
                  <h3 className="font-display text-4xl md:text-5xl tracking-[-0.02em] leading-[1.02] text-[var(--ink)] mb-6">
                    {s.title}
                  </h3>
                  <p className="text-[var(--ink-80)] text-base md:text-lg leading-relaxed mb-10">
                    {s.lead}
                  </p>

                  {/* Sub-practices */}
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mb-10">
                    {s.subServices.map((sub) => (
                      <div key={sub.name} className="border-t border-[var(--line-strong)] pt-4">
                        <div className="flex items-start gap-2 mb-2">
                          <Check className="w-4 h-4 text-[var(--accent)] mt-1 shrink-0" strokeWidth={2} />
                          <p className="font-display text-lg text-[var(--ink)] leading-tight">
                            {sub.name}
                          </p>
                        </div>
                        <p className="text-sm text-[var(--ink-70)] leading-relaxed pl-6">
                          {sub.blurb}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/contact"
                    className="link-slide text-[var(--ink)] inline-flex items-center gap-2"
                  >
                    <span className="text-sm uppercase tracking-[0.22em]">
                      Discuss this practice
                    </span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Closing CTA */}
      <section className="bg-[var(--bg)] border-t border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <h3 className="font-display text-3xl md:text-5xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)] max-w-2xl">
            Something in here fits your{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              inflection point?
            </span>
          </h3>
          <Link to="/contact" data-testid={SERVICES.ctaContact} className="btn-pill">
            <span>Send an enquiry</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </section>
  );
};

export default ServicesManifesto;
